import React, { useEffect } from 'react';
import { SideNav } from '@/components/dashboard/SideNav';
import { BottomNav } from '@/components/dashboard/BottomNav';
import DashboardLayout from '@/components/dashboard/DashboardLayout';
import DashboardHeader from '@/components/dashboard/DashboardHeader';
import { ProductHeader } from '@/components/shared/ProductHeader';
import { CoachChatSheet } from '@/components/shared/CoachChatSheet';
import { HelpSheet } from '@/components/help/HelpSheet';
import { useSettings } from '@/components/settings/lib/settings-context';
import { ShellProvider, NavBackdrop, useShell } from '@/contexts/shell-context';

export interface PageLayoutProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  isLoading?: boolean;
  showNavigation?: boolean;
  headerActions?: React.ReactNode;
  titleBadge?: React.ReactNode;
  compactContent?: boolean;
  /** Let page content use the full main column instead of the 1280px dashboard cap. */
  fullBleed?: boolean;
  coachHint?: string;
}

function CoachChatGate({ hint }: { hint?: string }) {
  const { settings } = useSettings();
  const { coachOpen, closeCoach } = useShell();
  const coachChatEnabled = settings.experimentalFeaturesEnabled === true;

  useEffect(() => {
    if (!coachChatEnabled && coachOpen) {
      closeCoach();
    }
  }, [coachChatEnabled, coachOpen, closeCoach]);

  if (!coachChatEnabled) {
    return null;
  }

  return <CoachChatSheet hint={hint} />;
}

export const PageLayout: React.FC<PageLayoutProps> = ({
  title,
  subtitle,
  children,
  isLoading = false,
  showNavigation = true,
  headerActions,
  compactContent = false,
  fullBleed = false,
  coachHint,
}) => {
  useEffect(() => {
    if (showNavigation) {
      return;
    }

    document.documentElement.classList.add('allow-document-scroll');
    document.body.classList.add('allow-document-scroll');
    const root = document.getElementById('root');
    root?.classList.add('allow-document-scroll');

    return () => {
      document.documentElement.classList.remove('allow-document-scroll');
      document.body.classList.remove('allow-document-scroll');
      root?.classList.remove('allow-document-scroll');
    };
  }, [showNavigation]);

  if (!showNavigation) {
    return (
      <div className="min-h-dvh bg-background">
        <DashboardLayout title={title} isLoading={isLoading} compact={compactContent} fullBleed={fullBleed}>
          <DashboardHeader title={title} subtitle={subtitle}>
            {headerActions}
          </DashboardHeader>
          {children}
        </DashboardLayout>
      </div>
    );
  }

  return (
    <ShellProvider>
      <div className="app-shell flex h-full min-h-0 flex-col overflow-hidden bg-background lg:h-dvh lg:flex-row">
        <NavBackdrop />
        <SideNav />

        <div className="main-area relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <ProductHeader sectionTitle={title} className="topbar" />

          <main className="page flex-1 min-h-0 overflow-x-hidden overflow-y-auto flex flex-col">
            <DashboardLayout title={title} isLoading={isLoading} compact={compactContent} fullBleed={fullBleed}>
              <DashboardHeader title={title} subtitle={subtitle}>
                {headerActions}
              </DashboardHeader>
              {children}
            </DashboardLayout>
          </main>

          <BottomNav />
        </div>

        <CoachChatGate hint={coachHint} />
        <HelpSheet />
      </div>
    </ShellProvider>
  );
};

export default PageLayout;
